/**
 * watchpoint-alarms-section.tsx — embeddable alarm list + inline create
 * for mounting inside WatchpointDetail. Pre-fills watchpointId + type,
 * hides the monitor selector in AlarmCreate.
 */
import { useState, useEffect } from 'react';
import { Plus, Bell } from 'lucide-react';
import { Button, Switch, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWatchpointAlarms,
  useDeleteAlarm,
  useToggleAlarm,
  type Alarm,
  type WatchpointType,
} from '@/api/healthcheck';
import { SeverityBadge } from './SeverityBadge';
import { AlarmEditDialog } from './AlarmEditDialog';
import { AlarmCreate } from './AlarmCreate';
import type { ConditionRow } from './condition-serializer';
import { RowMenu } from '../../components/RowMenu';
import { conditionSummary } from './condition-summary';
import { fmtRelTime } from '../shared';

interface Props {
  watchpointId: string;
  watchpointType: WatchpointType;
  /** External request to open the create form pre-seeded with rows (bump nonce to re-trigger). */
  prefill?: { rows: ConditionRow[]; nonce: number };
}

export function WatchpointAlarmsSection({ watchpointId, watchpointType, prefill }: Props) {
  const { show } = useToast();
  const { data: alarms = [], isLoading, isError, refetch } = useWatchpointAlarms(watchpointId);
  const toggleAlarm = useToggleAlarm();
  const deleteAlarm = useDeleteAlarm();

  const [showCreate, setShowCreate] = useState(false);
  const [editAlarm, setEditAlarm] = useState<Alarm | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Alarm | null>(null);
  const [seedRows, setSeedRows] = useState<ConditionRow[] | undefined>(undefined);

  // "Apply as threshold" from the baseline panel: open the form seeded with the row.
  useEffect(() => {
    if (!prefill) return;
    setSeedRows(prefill.rows);
    setShowCreate(true);
  }, [prefill?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Couldn't load alarms"
        actions={
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Failed to load alarm rules for this monitor.
      </Alert>
    );
  }

  if (isLoading) return <Skeleton style={{ height: 120 }} />;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
          Alert rules{' '}
          {alarms.length > 0 && (
            <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400 }}>
              ({alarms.length})
            </span>
          )}
        </span>
        {!showCreate && (
          <Button
            size="sm"
            variant="secondary"
            startIcon={<Plus size={13} />}
            onClick={() => setShowCreate(true)}
          >
            Add alert
          </Button>
        )}
      </div>

      {/* Inline create form */}
      {showCreate && (
        <div
          style={{
            border: '1px solid var(--vn-border-default)',
            borderRadius: 'var(--vn-radius-lg)',
            padding: 'var(--vn-space-4)',
            background: 'var(--vn-bg-subtle)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--vn-space-3)' }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
              New alert rule
            </span>
            <Button size="sm" variant="ghost" onClick={() => { setShowCreate(false); setSeedRows(undefined); }}>
              Cancel
            </Button>
          </div>
          <AlarmCreate
            key={prefill?.nonce ?? 'blank'}
            watchpointId={watchpointId}
            watchpointType={watchpointType}
            initialRows={seedRows}
            onSuccess={() => { setShowCreate(false); setSeedRows(undefined); }}
          />
        </div>
      )}

      {/* Alarm list */}
      {alarms.length === 0 && !showCreate ? (
        <EmptyState
          icon={<Bell size={24} />}
          title="No alert rules"
          description="Add an alert rule to get notified when this monitor fails."
          size="sm"
          actions={
            <Button
              size="sm"
              startIcon={<Plus size={13} />}
              onClick={() => setShowCreate(true)}
            >
              Add alert
            </Button>
          }
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {alarms.map((alarm) => (
            <AlarmRow
              key={alarm.id}
              alarm={alarm}
              onEdit={() => setEditAlarm(alarm)}
              onDelete={() => setPendingDelete(alarm)}
              onToggle={(is_active) =>
                toggleAlarm.mutate(
                  { id: alarm.id, is_active },
                  {
                    onError: (e) =>
                      show({ tone: 'danger', title: (e as Error)?.message || 'Update failed' }),
                  },
                )
              }
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      <AlarmEditDialog alarm={editAlarm} onClose={() => setEditAlarm(null)} />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        destructive
        title={`Delete "${pendingDelete?.name}"?`}
        description="This alarm rule will be permanently deleted."
        confirmLabel="Delete"
        onConfirm={() => {
          const alarm = pendingDelete!;
          setPendingDelete(null);
          deleteAlarm.mutate(alarm.id, {
            onError: (e) =>
              show({ tone: 'danger', title: (e as Error)?.message || 'Delete failed' }),
          });
        }}
      />
    </div>
  );
}

// ---- Alarm row card -------------------------------------------------------

interface AlarmRowProps {
  alarm: Alarm;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: (active: boolean) => void;
}

function AlarmRow({ alarm, onEdit, onDelete, onToggle }: AlarmRowProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 'var(--vn-space-3)',
        alignItems: 'center',
        padding: 'var(--vn-space-3)',
        border: '1px solid var(--vn-border-subtle)',
        borderRadius: 'var(--vn-radius-md)',
        background: 'var(--vn-bg-surface)',
      }}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-1)', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
          <span
            style={{
              fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'],
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {alarm.name}
          </span>
          <SeverityBadge severity={alarm.severity} />
          {!alarm.is_active && (
            <Badge tone="neutral" appearance="subtle" size="sm">Paused</Badge>
          )}
        </div>
        <div
          style={{
            fontSize: 'var(--vn-font-size-xs)',
            color: 'var(--vn-fg-muted)',
            fontFamily: 'var(--vn-font-mono)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {conditionSummary(alarm.conditions)}
        </div>
        <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          Updated {fmtRelTime(alarm.updated_at)} · after {alarm.consecutive_failures} failure
          {alarm.consecutive_failures !== 1 ? 's' : ''} · cooldown {alarm.cooldown_minutes}m
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <Switch
          checked={alarm.is_active}
          onCheckedChange={onToggle}
        />
        <RowMenu
          items={[
            { label: 'Edit', onSelect: onEdit },
            { label: 'Delete', danger: true, onSelect: onDelete },
          ]}
        />
      </div>
    </div>
  );
}
